import { useEffect, useMemo, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { extractErrorMessage } from '../lib/errors';
import * as XLSX from 'xlsx';

interface ObraCau {
  id: number;
  rrt_numero: string | null;
  prioridade: string | null;
  segmento: string | null;
  confianca: string | null;
  em_andamento: boolean | null;
  natureza_tecnica: string | null;
  data_inicio: string | null;
  data_fim: string | null;
  situacao: string | null;
  contratante: string | null;
  pessoa_fisica_juridica: string | null;
  documento: string | null;
  endereco: string | null;
  numero: string | null;
  bairro: string | null;
  cidade: string | null;
  uf: string | null;
  area_max: number | null;
  metragem_original: string | null;
  valor_contrato_max: number | null;
  atividades_rrt: string | null;
  grupos_rrt: string | null;
  profissional: string | null;
  empresa_profissional: string | null;
  criterio_classificacao: string | null;
  registros_consolidados: number | null;
  status: string | null;
  fonte: string | null;
  created_at: string;
  updated_at: string | null;
  importado_em: string | null;
  empresa_id: number | null;
}

interface Estatisticas {
  total: number;
  em_andamento: number;
  prioridade_alta: number;
  novas: number;
  execucao: number;
  projeto: number;
  comercial: number;
  institucional: number;
  residencial: number;
  vencendo_30dias: number;
  recentes_7dias: number;
  sem_lead: number;
  valor_total: number;
  bairros: { bairro: string | null; count: number }[];
  segmentos: { segmento: string | null; count: number }[];
}

interface RadarProps {
  password: string;
}

function formatBRL(value: number | null | undefined): string {
  if (value == null || value === 0) return '—';
  return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function formatArea(value: number | null | undefined): string {
  if (value == null) return '—';
  return `${value.toLocaleString('pt-BR', { maximumFractionDigits: 2 })} m²`;
}

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function isNova(createdAt: string): boolean {
  const d = new Date(createdAt);
  const diff = Date.now() - d.getTime();
  return diff < 7 * 24 * 60 * 60 * 1000;
}

function isVencendo(dataFim: string | null): boolean {
  if (!dataFim) return false;
  const d = new Date(dataFim);
  const diff = d.getTime() - Date.now();
  return diff > 0 && diff < 30 * 24 * 60 * 60 * 1000;
}

function BarraHorizontal({ label, value, max, cor, onClick }: { label: string; value: number; max: number; cor: string; onClick?: () => void }) {
  const pct = max > 0 ? (value / max) * 100 : 0;
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!onClick}
      className={`flex items-center gap-3 text-sm w-full ${onClick ? 'cursor-pointer hover:opacity-80 transition-opacity' : ''}`}
    >
      <span className="w-32 text-right text-xs text-gray-600 truncate whitespace-nowrap">{label}</span>
      <div className="flex-1 h-5 bg-gray-100 rounded-md overflow-hidden relative">
        <div
          className="h-full rounded-md transition-all duration-500"
          style={{ width: `${pct}%`, backgroundColor: cor }}
        />
      </div>
      <span className="w-8 text-xs font-semibold text-gray-700 text-right">{value}</span>
    </button>
  );
}

export default function ObrasRadar({ password }: RadarProps) {
  const [stats, setStats] = useState<Estatisticas | null>(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [statsError, setStatsError] = useState<string | null>(null);

  const [obras, setObras] = useState<ObraCau[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [obrasLoading, setObrasLoading] = useState(false);
  const [obrasError, setObrasError] = useState<string | null>(null);

  const [filtroSegmento, setFiltroSegmento] = useState('');
  const [filtroBairro, setFiltroBairro] = useState('');
  const [filtroPrioridade, setFiltroPrioridade] = useState('');
  const [filtroNatureza, setFiltroNatureza] = useState('');
  const [filtroAndamento, setFiltroAndamento] = useState<string>('');
  const [filtroRecentes, setFiltroRecentes] = useState(false);
  const [filtroVencendo, setFiltroVencendo] = useState(false);
  const [filtroSearch, setFiltroSearch] = useState('');
  const [offset, setOffset] = useState(0);
  const LIMIT = 50;

  const [aba, setAba] = useState<'dashboard' | 'tabela' | 'importar'>('dashboard');
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importData, setImportData] = useState<any[]>([]);
  const [importLoading, setImportLoading] = useState(false);
  const [importResult, setImportResult] = useState<{ ok: boolean; msg: string } | null>(null);
  const [importError, setImportError] = useState<string | null>(null);
  const [detalheObra, setDetalheObra] = useState<ObraCau | null>(null);
  const [gerarLeadsLoading, setGerarLeadsLoading] = useState(false);
  const [gerarLeadsResult, setGerarLeadsResult] = useState<{ ok: boolean; msg: string; total?: number } | null>(null);
  const [selectedObras, setSelectedObras] = useState<Set<number>>(new Set());
  const [avaliandoObraId, setAvaliandoObraId] = useState<number | null>(null);
  const [avaliarResult, setAvaliarResult] = useState<{ existente: boolean; oportunidade_id: number; titulo: string } | null>(null);

  const loadStats = useCallback(async () => {
    setStatsLoading(true);
    setStatsError(null);
    try {
      const { data, error } = await supabase.functions.invoke('crm-obras', {
        body: { action: 'estatisticas', password },
      });
      if (error) throw new Error(await extractErrorMessage(error));
      const parsed = data as Estatisticas & { error?: string };
      if (parsed?.error) throw new Error(parsed.error);
      setStats(parsed);
    } catch (e) {
      setStatsError(e instanceof Error ? e.message : 'Erro ao carregar estatísticas.');
    } finally {
      setStatsLoading(false);
    }
  }, [password]);

  const loadObras = useCallback(async () => {
    setObrasLoading(true);
    setObrasError(null);
    try {
      const { data, error } = await supabase.functions.invoke('crm-obras', {
        body: {
          action: 'listar',
          password,
          segmento: filtroSegmento || undefined,
          bairro: filtroBairro || undefined,
          prioridade: filtroPrioridade || undefined,
          natureza: filtroNatureza || undefined,
          em_andamento: filtroAndamento === '' ? undefined : filtroAndamento === 'true',
          recentes: filtroRecentes || undefined,
          vencendo: filtroVencendo || undefined,
          search: filtroSearch || undefined,
          limit: LIMIT,
          offset,
        },
      });
      if (error) throw new Error(await extractErrorMessage(error));
      const parsed = data as { obras: ObraCau[]; count: number; error?: string };
      if (parsed?.error) throw new Error(parsed.error);
      setObras(parsed.obras ?? []);
      setTotalCount(parsed.count ?? 0);
    } catch (e) {
      setObrasError(e instanceof Error ? e.message : 'Erro ao carregar obras.');
    } finally {
      setObrasLoading(false);
    }
  }, [password, filtroSegmento, filtroBairro, filtroPrioridade, filtroNatureza, filtroAndamento, filtroRecentes, filtroVencendo, filtroSearch, offset]);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  useEffect(() => {
    loadObras();
  }, [loadObras]);

  const abrirTabelaFiltrada = (filtros: {
    segmento?: string;
    bairro?: string;
    prioridade?: string;
    natureza?: string;
    andamento?: string;
    recentes?: boolean;
    vencendo?: boolean;
  }) => {
    setFiltroSegmento(filtros.segmento ?? '');
    setFiltroBairro(filtros.bairro ?? '');
    setFiltroPrioridade(filtros.prioridade ?? '');
    setFiltroNatureza(filtros.natureza ?? '');
    setFiltroAndamento(filtros.andamento ?? '');
    setFiltroRecentes(!!filtros.recentes);
    setFiltroVencendo(!!filtros.vencendo);
    setFiltroSearch('');
    setOffset(0);
    setAba('tabela');
  };

  const handleFile = async (file: File) => {
    setImportFile(file);
    setImportError(null);
    setImportResult(null);
    setImportData([]);
    try {
      const nome = file.name.toLowerCase();
      let rows: any[] = [];
      if (nome.endsWith('.csv') || nome.endsWith('.txt')) {
        const text = await file.text();
        rows = parseCSV(text);
      } else if (nome.endsWith('.xlsx') || nome.endsWith('.xls')) {
        const buffer = await file.arrayBuffer();
        const wb = XLSX.read(buffer, { type: 'array' });
        const sheetName = escolherAba(wb.SheetNames, wb.Sheets);
        const ws = wb.Sheets[sheetName];
        rows = XLSX.utils.sheet_to_json(ws, { header: 1 });
      } else {
        setImportError('Formato não suportado. Envie CSV, TXT ou Excel (.xlsx).');
        return;
      }
      const mapped = mapRowsToObras(rows);
      setImportData(mapped);
    } catch (e) {
      setImportError(e instanceof Error ? e.message : 'Erro ao ler arquivo.');
    }
  };

  const doImport = async () => {
    if (!importData.length) return;
    setImportLoading(true);
    setImportError(null);
    setImportResult(null);
    try {
      const { data, error } = await supabase.functions.invoke('crm-obras', {
        body: { action: 'importar', password, obras: importData },
      });
      if (error) throw new Error(await extractErrorMessage(error));
      const parsed = data as { importados?: number; pulados?: number; error?: string };
      if (parsed?.error) throw new Error(parsed.error);
      const importados = parsed.importados ?? 0;
      const pulados = parsed.pulados ?? 0;
      setImportResult({
        ok: true,
        msg: pulados > 0
          ? `${importados} obras importadas. ${pulados} já existiam no radar (ignoradas para não duplicar).`
          : `${importados} obras importadas com sucesso!`,
      });
      await loadStats();
      await loadObras();
    } catch (e) {
      setImportError(e instanceof Error ? e.message : 'Erro ao importar.');
    } finally {
      setImportLoading(false);
    }
  };

  const gerarLeads = async (obraIds?: number[]) => {
    setGerarLeadsLoading(true);
    setGerarLeadsResult(null);
    try {
      const { data, error } = await supabase.functions.invoke('crm-obras', {
        body: { action: 'gerar_leads', password, obra_ids: obraIds },
      });
      if (error) throw new Error(await extractErrorMessage(error));
      const parsed = data as any;
      if (parsed?.error) throw new Error(parsed.error);
      setGerarLeadsResult({
        ok: true,
        msg: parsed.mensagem ?? `${parsed.total ?? 0} obra(s) convertida(s) em leads.`,
        total: parsed.total ?? 0,
      });
      setSelectedObras(new Set());
      await loadStats();
      await loadObras();
    } catch (e) {
      setGerarLeadsResult({ ok: false, msg: e instanceof Error ? e.message : 'Erro ao gerar leads.' });
    } finally {
      setGerarLeadsLoading(false);
    }
  };

  const toggleObra = (id: number) => {
    setSelectedObras((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const atualizarStatus = async (id: number, status: string) => {
    try {
      const { error } = await supabase.functions.invoke('crm-obras', {
        body: { action: 'atualizar_status', password, obra_id: id, status },
      });
      if (error) throw new Error(await extractErrorMessage(error));
      setObras((prev) => prev.map((o) => (o.id === id ? { ...o, status } : o)));
      if (detalheObra?.id === id) setDetalheObra((prev) => prev ? { ...prev, status } : null);
    } catch (e) {
      setObrasError(e instanceof Error ? e.message : 'Erro ao atualizar status.');
    }
  };

  const avaliarOportunidade = async (obra: ObraCau) => {
    setAvaliandoObraId(obra.id);
    setAvaliarResult(null);
    try {
      const { data, error } = await supabase.functions.invoke('radar-admin', {
        body: { action: 'obra_para_oportunidade', password, obra_id: obra.id },
      });
      if (error) throw new Error(await extractErrorMessage(error));
      const parsed = data as { existente?: boolean; oportunidade?: { id: number; titulo: string }; error?: string };
      if (parsed?.error) throw new Error(parsed.error);
      setAvaliarResult({
        existente: !!parsed.existente,
        oportunidade_id: parsed.oportunidade?.id ?? 0,
        titulo: parsed.oportunidade?.titulo ?? '',
      });
    } catch (e) {
      setObrasError(e instanceof Error ? e.message : 'Erro ao criar oportunidade no Radar.');
    } finally {
      setAvaliandoObraId(null);
    }
  };

  const bairrosMax = useMemo(() => {
    if (!stats?.bairros?.length) return 1;
    return Math.max(...stats.bairros.map((b) => Number(b.count)));
  }, [stats]);

  const segmMax = useMemo(() => {
    if (!stats?.segmentos?.length) return 1;
    return Math.max(...stats.segmentos.map((s) => Number(s.count)));
  }, [stats]);

  const bairrosOptions = useMemo(() => {
    if (!stats?.bairros?.length) return [];
    return Array.from(new Set(stats.bairros.map((b) => b.bairro).filter(Boolean))).sort();
  }, [stats]);

  const segmentosOptions = useMemo(() => {
    if (!stats?.segmentos?.length) return [];
    return Array.from(new Set(stats.segmentos.map((s) => s.segmento).filter(Boolean))).sort();
  }, [stats]);

  return (
    <div className="space-y-6">
      {/* Cabeçalho */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 flex items-center justify-center bg-[#E8610A]/10 text-[#E8610A] rounded-xl">
            <i className="ri-radar-line text-lg"></i>
          </div>
          <div>
            <h2 className="font-bold text-gray-900">Radar de Obras PE</h2>
            <p className="text-sm text-gray-500">
              Inteligência comercial de obras, arquitetos e construtoras — CAU Pernambuco
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {stats?.recentes_7dias ? (
            <span className="inline-flex items-center gap-1.5 text-xs font-bold bg-red-50 text-red-600 border border-red-200 rounded-full px-3 py-1">
              <i className="ri-alarm-warning-line"></i>
              {stats.recentes_7dias} obra(s) nova(s)
            </span>
          ) : null}
        </div>
      </div>

      {/* Abas */}
      <div className="inline-flex items-center bg-gray-100 rounded-full px-1 py-1">
        <button
          onClick={() => setAba('dashboard')}
          className={`inline-flex items-center gap-2 text-sm font-semibold rounded-full px-4 py-2 transition-colors cursor-pointer whitespace-nowrap ${
            aba === 'dashboard' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          <i className="ri-dashboard-3-line"></i>
          Dashboard
        </button>
        <button
          onClick={() => setAba('tabela')}
          className={`inline-flex items-center gap-2 text-sm font-semibold rounded-full px-4 py-2 transition-colors cursor-pointer whitespace-nowrap ${
            aba === 'tabela' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          <i className="ri-table-line"></i>
          Obras
          {totalCount > 0 && (
            <span className="text-[11px] bg-gray-200 text-gray-600 rounded-full px-2 py-0.5">{totalCount}</span>
          )}
        </button>
        <button
          onClick={() => setAba('importar')}
          className={`inline-flex items-center gap-2 text-sm font-semibold rounded-full px-4 py-2 transition-colors cursor-pointer whitespace-nowrap ${
            aba === 'importar' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          <i className="ri-file-upload-line"></i>
          Importar
        </button>
      </div>

      {/* ===================== DASHBOARD ===================== */}
      {aba === 'dashboard' && (
        <div className="space-y-5">
          {statsError && (
            <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-4 py-3">{statsError}</div>
          )}

          {statsLoading && !stats ? (
            <div className="flex justify-center py-16">
              <div className="w-8 h-8 border-2 border-[#E8610A] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : stats ? (
            <>
              {/* KPIs */}
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
                <button
                  type="button"
                  onClick={() => abrirTabelaFiltrada({})}
                  className="bg-white rounded-xl border border-gray-200 p-4 text-left w-full cursor-pointer hover:border-[#E8610A]/40 transition-colors group"
                >
                  <p className="text-[11px] text-gray-500 uppercase tracking-wider font-semibold flex items-center justify-between">
                    Total de obras
                    <i className="ri-filter-3-line text-gray-300 group-hover:text-[#E8610A] transition-colors"></i>
                  </p>
                  <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
                  <p className="text-xs text-gray-400 mt-1">{stats.execucao} execução · {stats.projeto} projeto</p>
                </button>
                <button
                  type="button"
                  onClick={() => abrirTabelaFiltrada({ andamento: 'true' })}
                  className="bg-white rounded-xl border border-gray-200 p-4 text-left w-full cursor-pointer hover:border-[#E8610A]/40 transition-colors group"
                >
                  <p className="text-[11px] text-gray-500 uppercase tracking-wider font-semibold flex items-center justify-between">
                    Em andamento
                    <i className="ri-filter-3-line text-gray-300 group-hover:text-[#E8610A] transition-colors"></i>
                  </p>
                  <p className="text-2xl font-bold text-green-600">{stats.em_andamento}</p>
                  <p className="text-xs text-gray-400 mt-1">{stats.vencendo_30dias} vencendo em 30d</p>
                </button>
                <button
                  type="button"
                  onClick={() => abrirTabelaFiltrada({ prioridade: 'Alta' })}
                  className="bg-white rounded-xl border border-gray-200 p-4 text-left w-full cursor-pointer hover:border-[#E8610A]/40 transition-colors group"
                >
                  <p className="text-[11px] text-gray-500 uppercase tracking-wider font-semibold flex items-center justify-between">
                    Prioridade Alta
                    <i className="ri-filter-3-line text-gray-300 group-hover:text-[#E8610A] transition-colors"></i>
                  </p>
                  <p className="text-2xl font-bold text-red-500">{stats.prioridade_alta}</p>
                  <p className="text-xs text-gray-400 mt-1">Merece atenção imediata</p>
                </button>
                <button
                  type="button"
                  onClick={() => abrirTabelaFiltrada({ segmento: 'Comercial' })}
                  className="bg-white rounded-xl border border-gray-200 p-4 text-left w-full cursor-pointer hover:border-[#E8610A]/40 transition-colors group"
                >
                  <p className="text-[11px] text-gray-500 uppercase tracking-wider font-semibold flex items-center justify-between">
                    Comercial
                    <i className="ri-filter-3-line text-gray-300 group-hover:text-[#E8610A] transition-colors"></i>
                  </p>
                  <p className="text-2xl font-bold text-[#E8610A]">{stats.comercial}</p>
                  <p className="text-xs text-gray-400 mt-1">Foco principal HOP</p>
                </button>
                <button
                  type="button"
                  onClick={() => abrirTabelaFiltrada({ segmento: 'Institucional' })}
                  className="bg-white rounded-xl border border-gray-200 p-4 text-left w-full cursor-pointer hover:border-[#E8610A]/40 transition-colors group"
                >
                  <p className="text-[11px] text-gray-500 uppercase tracking-wider font-semibold flex items-center justify-between">
                    Institucional
                    <i className="ri-filter-3-line text-gray-300 group-hover:text-[#E8610A] transition-colors"></i>
                  </p>
                  <p className="text-2xl font-bold text-blue-600">{stats.institucional}</p>
                  <p className="text-xs text-gray-400 mt-1">Escolas, hospitais, órgãos</p>
                </button>
                <button
                  type="button"
                  onClick={() => abrirTabelaFiltrada({ segmento: 'Residencial' })}
                  className="bg-white rounded-xl border border-gray-200 p-4 text-left w-full cursor-pointer hover:border-[#E8610A]/40 transition-colors group"
                >
                  <p className="text-[11px] text-gray-500 uppercase tracking-wider font-semibold flex items-center justify-between">
                    Residencial
                    <i className="ri-filter-3-line text-gray-300 group-hover:text-[#E8610A] transition-colors"></i>
                  </p>
                  <p className="text-2xl font-bold text-amber-600">{stats.residencial}</p>
                  <p className="text-xs text-gray-400 mt-1">Cadeiras, área externa</p>
                </button>
              </div>

              {/* Alerta de leads + ação */}
              {(stats.sem_lead ?? 0) > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex flex-col sm:flex-row items-start sm:items-center gap-3">
                  <div className="w-10 h-10 flex items-center justify-center bg-amber-100 text-amber-700 rounded-xl shrink-0">
                    <i className="ri-flashlight-line text-lg"></i>
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-amber-900">{stats.sem_lead} obras ainda não viraram leads no CRM</p>
                    <p className="text-xs text-amber-700 mt-0.5">
                      Clique em "Gerar leads" para criar automaticamente empresas, contatos e oportunidades de todas as obras relevantes.
                    </p>
                  </div>
                  <button
                    onClick={() => gerarLeads()}
                    disabled={gerarLeadsLoading}
                    className="inline-flex items-center gap-2 text-sm font-semibold text-white bg-[#E8610A] hover:bg-[#C4500A] px-4 py-2.5 rounded-xl cursor-pointer whitespace-nowrap transition-colors disabled:opacity-60 shrink-0"
                  >
                    <i className={`${gerarLeadsLoading ? 'ri-loader-4-line animate-spin' : 'ri-user-add-line'}`}></i>
                    {gerarLeadsLoading ? 'Gerando...' : 'Gerar leads'}
                  </button>
                </div>
              )}

              {gerarLeadsResult && (
                <div className={`text-sm border rounded-lg px-4 py-3 ${gerarLeadsResult.ok ? 'text-green-700 bg-green-50 border-green-200' : 'text-red-600 bg-red-50 border-red-200'}`}>
                  {gerarLeadsResult.msg}
                </div>
              )}

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <div className="bg-white rounded-xl border border-gray-200 p-4">
                  <p className="text-[11px] text-gray-500 uppercase tracking-wider font-semibold mb-3">Por bairro</p>
                  <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
                    {stats.bairros.map((b) => (
                      <BarraHorizontal
                        key={b.bairro ?? 'Sem bairro'}
                        label={b.bairro ?? 'Sem bairro'}
                        value={Number(b.count)}
                        max={bairrosMax}
                        cor="#E8610A"
                        onClick={b.bairro ? () => abrirTabelaFiltrada({ bairro: b.bairro as string }) : undefined}
                      />
                    ))}
                  </div>
                </div>
                <div className="bg-white rounded-xl border border-gray-200 p-4">
                  <p className="text-[11px] text-gray-500 uppercase tracking-wider font-semibold mb-3">Por segmento</p>
                  <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
                    {stats.segmentos.map((s) => (
                      <BarraHorizontal
                        key={s.segmento ?? 'Sem segmento'}
                        label={s.segmento ?? 'Sem segmento'}
                        value={Number(s.count)}
                        max={segmMax}
                        cor="#0EA5E9"
                        onClick={s.segmento ? () => abrirTabelaFiltrada({ segmento: s.segmento as string }) : undefined}
                      />
                    ))}
                  </div>
                </div>
                <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-4">
                  <p className="text-[11px] text-gray-500 uppercase tracking-wider font-semibold">Indicadores rápidos</p>
                  <button
                    type="button"
                    onClick={() => abrirTabelaFiltrada({ recentes: true })}
                    className="flex items-center justify-between border-b border-gray-100 pb-3 w-full text-left cursor-pointer hover:bg-gray-50 rounded transition-colors px-1"
                  >
                    <span className="text-sm text-gray-600">Obras novas (7 dias)</span>
                    <span className={`text-sm font-bold ${stats.recentes_7dias > 0 ? 'text-red-500' : 'text-gray-900'}`}>
                      {stats.recentes_7dias}
                    </span>
                  </button>
                  <button
                    type="button"
                    onClick={() => abrirTabelaFiltrada({ vencendo: true })}
                    className="flex items-center justify-between border-b border-gray-100 pb-3 w-full text-left cursor-pointer hover:bg-gray-50 rounded transition-colors px-1"
                  >
                    <span className="text-sm text-gray-600">Vencendo em 30 dias</span>
                    <span className="text-sm font-bold text-amber-600">{stats.vencendo_30dias}</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => abrirTabelaFiltrada({ natureza: 'Execucao' })}
                    className="flex items-center justify-between border-b border-gray-100 pb-3 w-full text-left cursor-pointer hover:bg-gray-50 rounded transition-colors px-1"
                  >
                    <span className="text-sm text-gray-600">Em execução</span>
                    <span className="text-sm font-bold text-gray-900">{stats.execucao}</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => abrirTabelaFiltrada({ natureza: 'Projeto' })}
                    className="flex items-center justify-between border-b border-gray-100 pb-3 w-full text-left cursor-pointer hover:bg-gray-50 rounded transition-colors px-1"
                  >
                    <span className="text-sm text-gray-600">Em projeto</span>
                    <span className="text-sm font-bold text-gray-900">{stats.projeto}</span>
                  </button>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Valor contrato máx</span>
                    <span className="text-sm font-bold text-gray-900">{formatBRL(stats.valor_total)}</span>
                  </div>
                </div>
              </div>
            </>
          ) : null}
        </div>
      )}

      {/* ===================== TABELA ===================== */}
      {aba === 'tabela' && (
        <div className="space-y-5">
          {obrasError && (
            <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-4 py-3">{obrasError}</div>
          )}

          {/* Filtros */}
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              <div>
                <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1.5">Segmento</label>
                <select
                  value={filtroSegmento}
                  onChange={(e) => { setFiltroSegmento(e.target.value); setOffset(0); }}
                  className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm text-gray-800 focus:outline-none focus:border-[#E8610A] bg-white"
                >
                  <option value="">Todos</option>
                  {segmentosOptions.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1.5">Bairro</label>
                <select
                  value={filtroBairro}
                  onChange={(e) => { setFiltroBairro(e.target.value); setOffset(0); }}
                  className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm text-gray-800 focus:outline-none focus:border-[#E8610A] bg-white"
                >
                  <option value="">Todos</option>
                  {bairrosOptions.map((b) => <option key={b} value={b}>{b}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1.5">Prioridade</label>
                <select
                  value={filtroPrioridade}
                  onChange={(e) => { setFiltroPrioridade(e.target.value); setOffset(0); }}
                  className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm text-gray-800 focus:outline-none focus:border-[#E8610A] bg-white"
                >
                  <option value="">Todas</option>
                  <option value="Alta">Alta</option>
                  <option value="Media">Média</option>
                  <option value="Baixa">Baixa</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1.5">Natureza</label>
                <select
                  value={filtroNatureza}
                  onChange={(e) => { setFiltroNatureza(e.target.value); setOffset(0); }}
                  className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm text-gray-800 focus:outline-none focus:border-[#E8610A] bg-white"
                >
                  <option value="">Todas</option>
                  <option value="Execucao">Execução</option>
                  <option value="Projeto">Projeto</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1.5">Em andamento</label>
                <select
                  value={filtroAndamento}
                  onChange={(e) => { setFiltroAndamento(e.target.value); setOffset(0); }}
                  className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm text-gray-800 focus:outline-none focus:border-[#E8610A] bg-white"
                >
                  <option value="">Todos</option>
                  <option value="true">Sim</option>
                  <option value="false">Não</option>
                </select>
              </div>
              <div className="lg:col-span-3">
                <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1.5">Buscar</label>
                <input
                  type="text"
                  value={filtroSearch}
                  onChange={(e) => { setFiltroSearch(e.target.value); setOffset(0); }}
                  placeholder="Contratante, profissional, endereço, RRT..."
                  className="w-full border border-gray-200 rounded-xl px-4 py-2 text-sm text-gray-800 focus:outline-none focus:border-[#E8610A] focus:ring-2 focus:ring-[#E8610A]/20 bg-white"
                />
              </div>
            </div>
          </div>

          {(filtroRecentes || filtroVencendo) && (
            <div className="flex items-center gap-2 flex-wrap">
              {filtroRecentes && (
                <button
                  onClick={() => setFiltroRecentes(false)}
                  className="inline-flex items-center gap-1.5 text-xs font-semibold bg-[#E8610A]/10 text-[#E8610A] rounded-full px-3 py-1.5 cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-sparkling-line"></i> Novas (7 dias)
                  <i className="ri-close-line"></i>
                </button>
              )}
              {filtroVencendo && (
                <button
                  onClick={() => setFiltroVencendo(false)}
                  className="inline-flex items-center gap-1.5 text-xs font-semibold bg-amber-100 text-amber-700 rounded-full px-3 py-1.5 cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-alarm-warning-line"></i> Vencendo em 30d
                  <i className="ri-close-line"></i>
                </button>
              )}
              <button
                onClick={() => { setFiltroRecentes(false); setFiltroVencendo(false); }}
                className="text-xs font-semibold text-gray-500 hover:text-gray-700 cursor-pointer whitespace-nowrap"
              >
                Limpar
              </button>
            </div>
          )}

          {/* Tabela */}
          <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
            {/* Bulk actions bar */}
            {selectedObras.size > 0 && (
              <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 bg-amber-50/60">
                <span className="text-sm font-semibold text-amber-900">
                  {selectedObras.size} obra(s) selecionada(s)
                </span>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setSelectedObras(new Set())}
                    className="text-xs font-semibold text-gray-500 hover:text-gray-700 px-2 py-1 rounded-lg hover:bg-gray-100 cursor-pointer transition-colors"
                  >
                    Limpar
                  </button>
                  <button
                    onClick={() => gerarLeads(Array.from(selectedObras))}
                    disabled={gerarLeadsLoading}
                    className="inline-flex items-center gap-1.5 text-xs font-semibold text-white bg-[#E8610A] hover:bg-[#C4500A] px-3 py-1.5 rounded-lg cursor-pointer whitespace-nowrap transition-colors disabled:opacity-60"
                  >
                    <i className={`${gerarLeadsLoading ? 'ri-loader-4-line animate-spin' : 'ri-user-add-line'}`}></i>
                    {gerarLeadsLoading ? 'Gerando...' : 'Virar lead'}
                  </button>
                </div>
              </div>
            )}
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50/80 text-gray-500 text-xs font-semibold uppercase tracking-wider">
                    <th className="px-4 py-3 text-left w-10">
                      <input
                        type="checkbox"
                        checked={obras.length > 0 && obras.every((o) => selectedObras.has(o.id))}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedObras(new Set(obras.map((o) => o.id)));
                          } else {
                            setSelectedObras(new Set());
                          }
                        }}
                        className="cursor-pointer"
                      />
                    </th>
                    <th className="px-4 py-3 text-left w-24">RRT</th>
                    <th className="px-4 py-3 text-left">Contratante</th>
                    <th className="px-4 py-3 text-left">Arquiteto(a)</th>
                    <th className="px-4 py-3 text-left">Segmento</th>
                    <th className="px-4 py-3 text-left">Localização</th>
                    <th className="px-4 py-3 text-left">Natureza</th>
                    <th className="px-4 py-3 text-left">Início</th>
                    <th className="px-4 py-3 text-left">Fim</th>
                    <th className="px-4 py-3 text-right">Área</th>
                    <th className="px-4 py-3 text-right">Valor</th>
                    <th className="px-4 py-3 text-center w-10"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {obrasLoading && obras.length === 0 ? (
                    <tr>
                      <td colSpan={12} className="py-12 text-center">
                        <div className="w-8 h-8 border-2 border-[#E8610A] border-t-transparent rounded-full animate-spin mx-auto" />
                      </td>
                    </tr>
                  ) : obras.length === 0 ? (
                    <tr>
                      <td colSpan={12} className="py-12 text-center text-gray-400 text-sm">
                        Nenhuma obra encontrada com os filtros atuais.
                      </td>
                    </tr>
                  ) : (
                    obras.map((o) => (
                      <tr
                        key={o.id}
                        className="hover:bg-gray-50/50 transition-colors cursor-pointer"
                        onClick={() => setDetalheObra(o)}
                      >
                        <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                          <input
                            type="checkbox"
                            checked={selectedObras.has(o.id)}
                            onChange={() => toggleObra(o.id)}
                            className="cursor-pointer"
                          />
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <span className="font-mono font-bold text-gray-900 text-xs">{o.rrt_numero}</span>
                            {isNova(o.created_at) && (
                              <span className="text-[10px] font-bold bg-red-50 text-red-600 rounded px-1.5 py-0.5">NOVO</span>
                            )}
                            {o.empresa_id && (
                              <span className="text-[10px] font-bold bg-green-50 text-green-600 rounded px-1.5 py-0.5" title="Já é lead no CRM">
                                <i className="ri-check-line mr-0.5"></i>LEAD
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="font-medium text-gray-900 max-w-xs truncate">{o.contratante}</div>
                          <div className="text-[11px] text-gray-400">{o.pessoa_fisica_juridica} {o.documento}</div>
                        </td>
                        <td className="px-4 py-3">
                          {o.profissional ? (
                            <>
                              <div className="font-medium text-gray-800 max-w-[180px] truncate">{o.profissional}</div>
                              {o.empresa_profissional && (
                                <div className="text-[11px] text-gray-400 max-w-[180px] truncate">{o.empresa_profissional}</div>
                              )}
                            </>
                          ) : (
                            <span className="text-gray-300">—</span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-block text-[11px] font-semibold rounded-full px-2 py-0.5 ${
                            o.segmento?.includes('Comercial') ? 'bg-[#E8610A]/10 text-[#E8610A]' :
                            o.segmento?.includes('Institucional') ? 'bg-blue-50 text-blue-700' :
                            'bg-amber-50 text-amber-700'
                          }`}>
                            {o.segmento}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-gray-700">
                          <div>{o.bairro ?? '—'}</div>
                          <div className="text-[11px] text-gray-400">{[o.cidade, o.uf].filter(Boolean).join(' - ')}</div>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center gap-1 text-[11px] font-semibold rounded-full px-2 py-0.5 ${
                            o.natureza_tecnica === 'Execucao' ? 'bg-green-50 text-green-700' : 'bg-purple-50 text-purple-700'
                          }`}>
                            <i className={`${o.natureza_tecnica === 'Execucao' ? 'ri-hammer-line' : 'ri-pencil-ruler-line'}`}></i>
                            {o.natureza_tecnica}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-gray-600">{formatDate(o.data_inicio)}</td>
                        <td className="px-4 py-3">
                          <span className={isVencendo(o.data_fim) ? 'text-amber-600 font-semibold text-xs' : 'text-gray-600'}>
                            {formatDate(o.data_fim)}
                            {isVencendo(o.data_fim) && <span className="ml-1 text-[10px] font-bold bg-amber-50 text-amber-600 rounded px-1">30d</span>}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right text-gray-600">{formatArea(o.area_max)}</td>
                        <td className="px-4 py-3 text-right font-semibold text-gray-900">{formatBRL(o.valor_contrato_max)}</td>
                        <td className="px-4 py-3 text-center">
                          <i className="ri-arrow-right-s-line text-gray-400"></i>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            {/* Paginação */}
            {totalCount > LIMIT && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100">
                <span className="text-xs text-gray-500">
                  Mostrando {offset + 1}–{Math.min(offset + LIMIT, totalCount)} de {totalCount}
                </span>
                <div className="flex items-center gap-2">
                  <button
                    disabled={offset === 0}
                    onClick={() => setOffset(Math.max(0, offset - LIMIT))}
                    className="inline-flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-40 cursor-pointer"
                  >
                    <i className="ri-arrow-left-s-line"></i> Anterior
                  </button>
                  <button
                    disabled={offset + LIMIT >= totalCount}
                    onClick={() => setOffset(offset + LIMIT)}
                    className="inline-flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-40 cursor-pointer"
                  >
                    Próximo <i className="ri-arrow-right-s-line"></i>
                  </button>
                </div>
              </div>
            )}
          </div>

          {gerarLeadsResult && (
            <div className={`text-sm border rounded-lg px-4 py-3 ${gerarLeadsResult.ok ? 'text-green-700 bg-green-50 border-green-200' : 'text-red-600 bg-red-50 border-red-200'}`}>
              {gerarLeadsResult.msg}
            </div>
          )}
        </div>
      )}

      {/* ===================== IMPORTAR ===================== */}
      {aba === 'importar' && (
        <div className="space-y-5">
          <div className="bg-white rounded-2xl border border-gray-200 p-5">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center bg-green-50 text-green-600 rounded-xl">
                <i className="ri-file-excel-2-line text-lg"></i>
              </div>
              <div>
                <h3 className="font-bold text-gray-900">Importar planilha do CAU</h3>
                <p className="text-sm text-gray-500">
                  Suba o arquivo de RRTs e o sistema separa automaticamente o que é comercial/corporativo do que é residencial.
                </p>
              </div>
            </div>

            <div className="border-2 border-dashed border-gray-200 rounded-xl p-6 text-center">
              <input
                id="cau-obra-file"
                type="file"
                accept=".csv,.txt,.xlsx,.xls"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) handleFile(f);
                }}
                className="hidden"
              />
              <label htmlFor="cau-obra-file" className="cursor-pointer inline-flex flex-col items-center gap-2">
                <div className="w-12 h-12 flex items-center justify-center bg-gray-100 text-gray-500 rounded-full">
                  <i className="ri-upload-cloud-2-line text-2xl"></i>
                </div>
                <span className="text-sm font-semibold text-gray-700">
                  {importFile?.name ?? 'Clique para escolher o arquivo'}
                </span>
                <span className="text-xs text-gray-400">CSV, TXT ou Excel (.xlsx)</span>
              </label>
            </div>

            {importData.length > 0 && (
              <div className="mt-4">
                <div className="flex items-center gap-3 mb-3">
                  <span className="text-sm text-gray-700">
                    <strong>{importData.length}</strong> obras lidas do arquivo
                  </span>
                  <button
                    onClick={doImport}
                    disabled={importLoading}
                    className="inline-flex items-center gap-2 text-sm font-semibold text-white bg-green-600 hover:bg-green-700 px-4 py-2 rounded-xl cursor-pointer whitespace-nowrap transition-colors disabled:opacity-60"
                  >
                    <i className={`${importLoading ? 'ri-loader-4-line animate-spin' : 'ri-magic-line'}`}></i>
                    {importLoading ? 'Importando...' : 'Importar obras'}
                  </button>
                </div>
                <div className="max-h-60 overflow-y-auto border border-gray-200 rounded-xl">
                  <table className="w-full text-xs">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr>
                        <th className="px-3 py-2 text-left">RRT</th>
                        <th className="px-3 py-2 text-left">Contratante</th>
                        <th className="px-3 py-2 text-left">Segmento</th>
                        <th className="px-3 py-2 text-left">Bairro</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {importData.slice(0, 20).map((r, i) => (
                        <tr key={i}>
                          <td className="px-3 py-2 font-mono">{r.rrt_numero}</td>
                          <td className="px-3 py-2">{r.contratante}</td>
                          <td className="px-3 py-2">{r.segmento}</td>
                          <td className="px-3 py-2">{r.bairro}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {importData.length > 20 && (
                    <p className="text-xs text-gray-400 px-3 py-2">...e mais {importData.length - 20} obras</p>
                  )}
                </div>
              </div>
            )}

            {importError && (
              <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-4 py-3 mt-4">{importError}</div>
            )}
            {importResult && (
              <div className={`text-sm border rounded-lg px-4 py-3 mt-4 ${importResult.ok ? 'text-green-700 bg-green-50 border-green-200' : 'text-red-600 bg-red-50 border-red-200'}`}>
                {importResult.msg}
              </div>
            )}
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-800">
            <p className="font-semibold mb-1">Como funciona a importação</p>
            <p className="text-amber-700 leading-relaxed mb-2">
              O sistema lê o arquivo e classifica cada obra: <strong>Comercial/Corporativo</strong> e <strong>Institucional</strong> vão pro seu radar principal (mesas, cadeiras, divisórias). <strong>Residencial</strong> fica em plano secundário (cadeiras de área externa, jantar). Obras em execução são prioridade porque já precisam de mobiliário!
            </p>
            <p className="text-amber-700 leading-relaxed">
              <strong>Dica:</strong> Depois de importar, vá na aba <strong>Dashboard</strong> e clique em <strong>"Gerar leads"</strong> para converter as obras em empresas, contatos e oportunidades no CRM automaticamente.
            </p>
          </div>
        </div>
      )}

      {/* ===================== MODAL DETALHE ===================== */}
      {detalheObra && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-2xl border border-gray-200 w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-xl">
            <div className="sticky top-0 bg-white px-6 py-4 border-b border-gray-100 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 flex items-center justify-center bg-[#E8610A]/10 text-[#E8610A] rounded-lg">
                  <i className="ri-building-4-line"></i>
                </div>
                <div>
                  <h3 className="font-bold text-gray-900 text-sm">Obra {detalheObra.rrt_numero}</h3>
                  <p className="text-xs text-gray-500">{detalheObra.contratante}</p>
                </div>
              </div>
              <button
                onClick={() => setDetalheObra(null)}
                className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg cursor-pointer"
              >
                <i className="ri-close-line"></i>
              </button>
            </div>
            <div className="p-6 space-y-5">
              <div className="flex flex-wrap gap-2">
                <span className={`text-[11px] font-bold rounded-full px-2.5 py-1 ${
                  detalheObra.prioridade === 'Alta' ? 'bg-red-50 text-red-600' : 'bg-gray-50 text-gray-600'
                }`}>
                  Prioridade {detalheObra.prioridade}
                </span>
                <span className={`text-[11px] font-bold rounded-full px-2.5 py-1 ${
                  detalheObra.natureza_tecnica === 'Execucao' ? 'bg-green-50 text-green-700' : 'bg-purple-50 text-purple-700'
                }`}>
                  {detalheObra.natureza_tecnica}
                </span>
                {detalheObra.em_andamento && (
                  <span className="text-[11px] font-bold bg-blue-50 text-blue-700 rounded-full px-2.5 py-1">
                    Em andamento
                  </span>
                )}
                {isNova(detalheObra.created_at) && (
                  <span className="text-[11px] font-bold bg-red-50 text-red-600 rounded-full px-2.5 py-1">
                    NOVA
                  </span>
                )}
                {isVencendo(detalheObra.data_fim) && (
                  <span className="text-[11px] font-bold bg-amber-50 text-amber-600 rounded-full px-2.5 py-1">
                    Vencendo em 30d
                  </span>
                )}
                {detalheObra.empresa_id && (
                  <span className="text-[11px] font-bold bg-green-50 text-green-600 rounded-full px-2.5 py-1">
                    <i className="ri-check-line mr-0.5"></i>Lead no CRM
                  </span>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="bg-gray-50 rounded-xl p-3">
                  <p className="text-[11px] text-gray-500 uppercase font-semibold">Segmento</p>
                  <p className="text-sm font-semibold text-gray-900">{detalheObra.segmento}</p>
                </div>
                <div className="bg-gray-50 rounded-xl p-3">
                  <p className="text-[11px] text-gray-500 uppercase font-semibold">Confiança</p>
                  <p className="text-sm font-semibold text-gray-900">{detalheObra.confianca}</p>
                </div>
                <div className="bg-gray-50 rounded-xl p-3">
                  <p className="text-[11px] text-gray-500 uppercase font-semibold">Área máxima</p>
                  <p className="text-sm font-semibold text-gray-900">{formatArea(detalheObra.area_max)}</p>
                </div>
                <div className="bg-gray-50 rounded-xl p-3">
                  <p className="text-[11px] text-gray-500 uppercase font-semibold">Valor contrato</p>
                  <p className="text-sm font-semibold text-gray-900">{formatBRL(detalheObra.valor_contrato_max)}</p>
                </div>
                <div className="bg-gray-50 rounded-xl p-3">
                  <p className="text-[11px] text-gray-500 uppercase font-semibold">Início</p>
                  <p className="text-sm font-semibold text-gray-900">{formatDate(detalheObra.data_inicio)}</p>
                </div>
                <div className="bg-gray-50 rounded-xl p-3">
                  <p className="text-[11px] text-gray-500 uppercase font-semibold">Fim</p>
                  <p className="text-sm font-semibold text-gray-900">{formatDate(detalheObra.data_fim)}</p>
                </div>
              </div>

              <div className="space-y-2">
                <p className="text-[11px] text-gray-500 uppercase font-semibold">Endereço</p>
                <p className="text-sm text-gray-800">{detalheObra.endereco}, {detalheObra.numero} — {detalheObra.bairro}, {detalheObra.cidade}-{detalheObra.uf}</p>
              </div>

              <div className="space-y-2">
                <p className="text-[11px] text-gray-500 uppercase font-semibold">Profissional</p>
                <p className="text-sm text-gray-800">{detalheObra.profissional}</p>
                {detalheObra.empresa_profissional && (
                  <p className="text-xs text-gray-500">{detalheObra.empresa_profissional}</p>
                )}
              </div>

              <div className="space-y-2">
                <p className="text-[11px] text-gray-500 uppercase font-semibold">Critério da classificação</p>
                <p className="text-sm text-gray-700 bg-amber-50/50 rounded-lg p-3">{detalheObra.criterio_classificacao}</p>
              </div>

              <div className="flex items-center gap-3 pt-2 flex-wrap">
                {!detalheObra.empresa_id && (
                  <button
                    onClick={() => { gerarLeads([detalheObra.id]); setDetalheObra(null); }}
                    disabled={gerarLeadsLoading}
                    className="inline-flex items-center gap-2 text-sm font-semibold text-white bg-[#E8610A] hover:bg-[#C4500A] px-4 py-2 rounded-xl cursor-pointer whitespace-nowrap transition-colors disabled:opacity-60"
                  >
                    <i className={`${gerarLeadsLoading ? 'ri-loader-4-line animate-spin' : 'ri-user-add-line'}`}></i>
                    {gerarLeadsLoading ? 'Gerando...' : 'Virar lead no CRM'}
                  </button>
                )}
                <button
                  onClick={() => avaliarOportunidade(detalheObra)}
                  disabled={avaliandoObraId === detalheObra.id}
                  className="inline-flex items-center gap-2 text-sm font-semibold text-[#E8610A] bg-[#E8610A]/10 hover:bg-[#E8610A]/20 px-4 py-2 rounded-xl cursor-pointer whitespace-nowrap transition-colors disabled:opacity-60"
                >
                  <i className={`${avaliandoObraId === detalheObra.id ? 'ri-loader-4-line animate-spin' : 'ri-radar-line'}`}></i>
                  Avaliar como oportunidade
                </button>
                <button
                  onClick={() => atualizarStatus(detalheObra.id, 'contatado')}
                  className="inline-flex items-center gap-2 text-sm font-semibold text-white bg-[#E8610A] hover:bg-[#C4500A] px-4 py-2 rounded-xl cursor-pointer whitespace-nowrap transition-colors"
                >
                  <i className="ri-phone-line"></i>
                  Marcar contatado
                </button>
                <button
                  onClick={() => atualizarStatus(detalheObra.id, 'arquivado')}
                  className="inline-flex items-center gap-2 text-sm font-semibold text-gray-600 bg-gray-100 hover:bg-gray-200 px-4 py-2 rounded-xl cursor-pointer whitespace-nowrap transition-colors"
                >
                  <i className="ri-archive-line"></i>
                  Arquivar
                </button>
                <button
                  onClick={() => atualizarStatus(detalheObra.id, 'novo')}
                  className="inline-flex items-center gap-2 text-sm font-semibold text-gray-600 bg-gray-100 hover:bg-gray-200 px-4 py-2 rounded-xl cursor-pointer whitespace-nowrap transition-colors"
                >
                  <i className="ri-arrow-go-back-line"></i>
                  Resetar
                </button>
              </div>

              {avaliarResult && (
                <div className={`mt-4 text-sm border rounded-lg px-4 py-3 ${avaliarResult.existente ? 'text-amber-700 bg-amber-50 border-amber-200' : 'text-green-700 bg-green-50 border-green-200'}`}>
                  {avaliarResult.existente ? 'Esta obra já possui uma oportunidade no Radar.' : 'Oportunidade criada no Radar.'}{' '}
                  <Link to={`/painel/radar/oportunidades/${avaliarResult.oportunidade_id}`} className="font-semibold underline">
                    Abrir oportunidade
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function escolherAba(names: string[], sheets: Record<string, any>): string {
  if (!names.length) return '';
  const linhas = (n: string) => {
    const ref = sheets[n]?.['!ref'] ?? 'A1';
    const last = ref.split(':')[1] ?? ref;
    const num = parseInt(last.replace(/[A-Z]/g, ''), 10);
    return Number.isNaN(num) ? 0 : num;
  };
  const consolidadas = names.filter((n) => /obras|todas|consolid/i.test(n));
  if (consolidadas.length) return consolidadas.sort((a, b) => linhas(b) - linhas(a))[0];
  return names.reduce((best, n) => (linhas(n) > linhas(best) ? n : best), names[0]);
}

function parseCSV(text: string): any[][] {
  const lines = text.trim().split('\n');
  return lines.map((line) => {
    const result: string[] = [];
    let current = '';
    let inQuotes = false;
    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      if (char === '"') {
        if (inQuotes && line[i + 1] === '"') {
          current += '"';
          i++;
        } else {
          inQuotes = !inQuotes;
        }
      } else if (char === ',' && !inQuotes) {
        result.push(current.trim());
        current = '';
      } else {
        current += char;
      }
    }
    result.push(current.trim());
    return result;
  });
}

function parseDateBR(val: string): string | null {
  if (!val) return null;
  const m = val.trim().match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (!m) return null;
  const [_, d, mo, y] = m;
  return `${y}-${mo.padStart(2, '0')}-${d.padStart(2, '0')}`;
}

function parseNumber(val: string): number | null {
  if (!val) return null;
  const cleaned = val.replace(/\./g, '').replace(/,/g, '.').replace(/[^\d.]/g, '');
  const n = parseFloat(cleaned);
  return Number.isNaN(n) ? null : n;
}

function detectSegmento(row: string[]): string {
  const text = row.join(' ').toLowerCase();
  if (text.includes('instituto') || text.includes('prefeitura') || text.includes('diocese') || text.includes('secretaria') || text.includes('federal') || text.includes('municipal') || text.includes('escola') || text.includes('hospital') || text.includes('clinica') || text.includes('industria')) {
    return 'Comercial/Institucional provavel';
  }
  if (text.includes('residencial') || text.includes('casa') || text.includes('apartamento') || text.includes('home') || text.includes('condominio residencial') || text.includes('conjunto residencial')) {
    return 'Residencial';
  }
  return 'Comercial/Corporativo provavel';
}

function mapRowsToObras(rows: any[]): any[] {
  if (rows.length < 2) return [];
  let header: string[] = [];
  let dataRows: any[] = [];
  const norm = (s: string) => s.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  if (Array.isArray(rows[0]) && typeof rows[0][0] === 'string') {
    header = rows[0].map((h: any) => norm(String(h).toLowerCase().trim()));
    dataRows = rows.slice(1);
  } else {
    dataRows = rows;
  }

  const getCol = (names: string[]) => {
    for (const n of names) {
      const idx = header.findIndex((h) => h.includes(n));
      if (idx >= 0) return idx;
    }
    return -1;
  };

  const rrtIdx = getCol(['rrt', 'registro']);
  const contratanteIdx = getCol(['contratante', 'empresa', 'cliente']);
  const segmentoIdx = getCol(['segmento', 'tipo']);
  const bairroIdx = getCol(['bairro']);
  const dataInicioIdx = getCol(['inicio', 'data inicio']);
  const dataFimIdx = getCol(['fim', 'data fim', 'termino']);
  const areaIdx = getCol(['area', 'metragem']);
  const valorIdx = getCol(['valor', 'contrato']);
  const naturezaIdx = getCol(['natureza', 'tecnica']);
  const enderecoIdx = getCol(['endereco', 'logradouro']);
  const numeroIdx = getCol(['numero', 'nº']);
  const docIdx = getCol(['documento', 'cnpj', 'cpf']);
  const pfPjIdx = getCol(['pessoa', 'pj', 'pf']);
  const profissionalIdx = getCol(['profissional', 'arquiteto']);
  const empresaProfIdx = getCol(['empresa do profissional', 'empresa(s) do profissional', 'empresas do profissional']);
  const cidadeIdx = getCol(['cidade', 'municipio']);
  const ufIdx = getCol(['uf', 'estado']);
  const criterioIdx = getCol(['criterio', 'classificacao']);
  const andamentoIdx = getCol(['andamento', 'ativo']);
  const situacaoIdx = getCol(['situacao', 'status']);
  const prioridadeIdx = getCol(['prioridade']);
  const confiancaIdx = getCol(['confianca']);

  return dataRows.map((row) => {
    const rawSegmento = segmentoIdx >= 0 ? String(row[segmentoIdx] ?? '') : '';
    const segmento = rawSegmento || detectSegmento(row);
    const areaVal = areaIdx >= 0 ? parseNumber(String(row[areaIdx] ?? '')) : null;
    const valorVal = valorIdx >= 0 ? parseNumber(String(row[valorIdx] ?? '')) : null;
    const inicio = dataInicioIdx >= 0 ? parseDateBR(String(row[dataInicioIdx] ?? '')) : null;
    const fim = dataFimIdx >= 0 ? parseDateBR(String(row[dataFimIdx] ?? '')) : null;
    const andamentoRaw = andamentoIdx >= 0 ? String(row[andamentoIdx] ?? '').toLowerCase() : '';
    const emAndamento = andamentoRaw.includes('sim') || andamentoRaw.includes('ativo') || andamentoRaw.includes('true') || andamentoRaw.includes('1');
    const natureza = naturezaIdx >= 0 ? String(row[naturezaIdx] ?? '').replace(/ç/g, 'c').replace(/ã/g, 'a').replace(/õ/g, 'o').replace(/á/g, 'a').replace(/é/g, 'e').replace(/í/g, 'i').replace(/ó/g, 'o').replace(/ú/g, 'u') : 'Execucao';

    return {
      rrt_numero: rrtIdx >= 0 ? String(row[rrtIdx] ?? '').trim() : null,
      contratante: contratanteIdx >= 0 ? String(row[contratanteIdx] ?? '').trim() : null,
      segmento,
      bairro: bairroIdx >= 0 ? String(row[bairroIdx] ?? '').trim() : null,
      cidade: cidadeIdx >= 0 ? String(row[cidadeIdx] ?? '').trim() : null,
      uf: ufIdx >= 0 ? String(row[ufIdx] ?? '').trim().toUpperCase() : null,
      data_inicio: inicio,
      data_fim: fim,
      area_max: areaVal,
      valor_contrato_max: valorVal,
      natureza_tecnica: natureza.charAt(0).toUpperCase() + natureza.slice(1),
      em_andamento: emAndamento,
      endereco: enderecoIdx >= 0 ? String(row[enderecoIdx] ?? '').trim() : null,
      numero: numeroIdx >= 0 ? String(row[numeroIdx] ?? '').trim() : null,
      documento: docIdx >= 0 ? String(row[docIdx] ?? '').trim() : null,
      pessoa_fisica_juridica: pfPjIdx >= 0 ? String(row[pfPjIdx] ?? '').trim() : null,
      profissional: profissionalIdx >= 0 ? String(row[profissionalIdx] ?? '').trim() : null,
      empresa_profissional: empresaProfIdx >= 0 ? String(row[empresaProfIdx] ?? '').trim() : null,
      criterio_classificacao: criterioIdx >= 0 ? String(row[criterioIdx] ?? '').trim() : null,
      situacao: situacaoIdx >= 0 ? String(row[situacaoIdx] ?? '').trim() : 'VALIDOS',
      prioridade: prioridadeIdx >= 0 ? String(row[prioridadeIdx] ?? '').trim() : 'Media',
      confianca: confiancaIdx >= 0 ? String(row[confiancaIdx] ?? '').trim() : 'Media',
      status: 'novo',
      fonte: 'CAU-PE-IMPORT',
    };
  }).filter((o) => o.rrt_numero || o.contratante);
}